import { useState } from "react";
import { router } from "@inertiajs/react";
import { PlayIcon, PauseIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { Spinner } from "@components/ui/spinner";
import { usePauseCampaign, useEnableCampaign } from "@components/ads/hooks/useCampaigns";

type Status = "draft" | "active" | "paused" | "completed";

interface CampaignStatusToggleProps {
  campaignId: number;
  status: Status;
}

// Pause/Enable button for a single campaign card. Optimistic by way of local
// pending state — we show the target status + a spinner immediately, then
// trigger an Inertia partial reload on success so the parent props (status
// badge, status_counts) reflect the new server-of-record state. Server
// updates the DB synchronously and enqueues the Google Ads API call, so we
// don't poll or stream — the response is enough.
export default function CampaignStatusToggle({ campaignId, status }: CampaignStatusToggleProps) {
  const [pendingTarget, setPendingTarget] = useState<Status | null>(null);

  const handleSuccess = () =>
    router.reload({ only: ["campaigns", "status_counts"], onFinish: () => setPendingTarget(null) });

  const handleError = (target: Status) => (err: Error) => {
    setPendingTarget(null);
    toast.error(`Failed to ${target === "paused" ? "pause" : "enable"} campaign: ${err.message}`);
  };

  const pause = usePauseCampaign(campaignId, {
    onSuccess: handleSuccess,
    onError: handleError("paused"),
  });
  const enable = useEnableCampaign(campaignId, {
    onSuccess: handleSuccess,
    onError: handleError("active"),
  });

  if (status !== "active" && status !== "paused") return null;

  const isPending = pause.isPending || enable.isPending || pendingTarget !== null;

  if (status === "active") {
    return (
      <button
        type="button"
        data-testid={`campaign-pause-button-${campaignId}`}
        disabled={isPending}
        onClick={() => {
          setPendingTarget("paused");
          pause.mutate();
        }}
        className="bg-white border border-neutral-300 rounded-lg px-3 py-2 text-sm leading-[18px] text-base-500 flex items-center justify-center gap-2 hover:bg-neutral-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isPending ? (
          <>
            <Spinner className="size-4" />
            Pausing…
          </>
        ) : (
          <>
            <PauseIcon className="w-4 h-4 text-base-600" />
            Pause
          </>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={`campaign-enable-button-${campaignId}`}
      disabled={isPending}
      onClick={() => {
        setPendingTarget("active");
        enable.mutate();
      }}
      className="bg-white border border-neutral-300 rounded-lg px-3 py-2 text-sm leading-[18px] text-base-500 flex items-center justify-center gap-2 hover:bg-neutral-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isPending ? (
        <>
          <Spinner className="size-4" />
          Enabling…
        </>
      ) : (
        <>
          <PlayIcon className="w-4 h-4 text-base-600" />
          Enable
        </>
      )}
    </button>
  );
}
